import { useEffect, useState, useCallback } from "react";
import { CreditCard, CheckCircle2, Clock, Ban, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";

const STATUS_STYLES = {
  OPEN: "bg-champagne-light text-champagne-dark",
  PAID: "bg-sage-light text-sage",
  VOID: "bg-red-950/60 text-red-300",
};

export default function Payments() {
  const [invoices, setInvoices] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);

  const load = useCallback(() => {
    Promise.all([api.get("/invoices"), api.get("/cases/mine")])
      .then(([inv, c]) => { setInvoices(inv.data); setCases(c.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const caseTitle = (id) => cases.find((c) => c.case_id === id)?.title;

  const pay = async (inv) => {
    setPayingId(inv.invoice_id);
    try {
      const res = await api.post(`/invoices/${inv.invoice_id}/checkout`, { origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout.");
      setPayingId(null);
    }
  };

  const open = invoices.filter((i) => i.status === "OPEN");
  const history = invoices.filter((i) => i.status !== "OPEN");
  const outstanding = open.reduce((s, i) => s + i.amount, 0);
  const totalPaid = history.filter((i) => i.status === "PAID").reduce((s, i) => s + i.amount, 0);

  if (loading) {
    return <div className="py-16 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-teal border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="portal-payments-page">
      <div>
        <h1 className="text-2xl font-serif font-semibold text-navy tracking-tight">Payments</h1>
        <p className="mt-1 text-sm text-mutedink">Pay invoices securely by card and see exactly how every payment was applied. Government and third-party fees are always labeled with who receives them.</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-surface border border-line rounded-2xl p-5" data-testid="outstanding-summary">
          <p className="text-xs font-bold tracking-[0.1em] uppercase text-champagne-dark flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Outstanding</p>
          <p className="mt-2 text-2xl font-serif font-semibold text-navy">${outstanding.toFixed(2)}</p>
        </div>
        <div className="bg-surface border border-line rounded-2xl p-5" data-testid="paid-summary">
          <p className="text-xs font-bold tracking-[0.1em] uppercase text-sage flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Total paid</p>
          <p className="mt-2 text-2xl font-serif font-semibold text-navy">${totalPaid.toFixed(2)}</p>
        </div>
      </div>

      <section data-testid="open-invoices">
        <h2 className="text-base font-sans font-semibold text-navy mb-4">Invoices to pay</h2>
        {open.length === 0 ? (
          <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">Nothing due right now. When your team issues an invoice, it will appear here with a secure payment link.</p>
        ) : (
          <div className="space-y-3">
            {open.map((inv) => (
              <div key={inv.invoice_id} className="bg-surface border border-line rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`invoice-row-${inv.invoice_id}`}>
                <div className="h-10 w-10 rounded-xl bg-champagne-light flex items-center justify-center flex-shrink-0">
                  <CreditCard className="h-5 w-5 text-champagne-dark" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-navy">{inv.description}</p>
                  <p className="text-xs text-mutedink mt-0.5">
                    {inv.fee_label} · paid to <span className="font-medium text-ink">{inv.recipient}</span>
                    {caseTitle(inv.case_id) && <> · applied to: {caseTitle(inv.case_id)}</>}
                    {" · issued "}{new Date(inv.created_at).toLocaleDateString()}
                  </p>
                </div>
                <p className="text-lg font-serif font-semibold text-navy">${inv.amount.toFixed(2)}</p>
                <Button onClick={() => pay(inv)} disabled={payingId === inv.invoice_id} className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid={`pay-button-${inv.invoice_id}`}>
                  {payingId === inv.invoice_id ? "Redirecting..." : "Pay securely"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section data-testid="payment-history">
        <h2 className="text-base font-sans font-semibold text-navy mb-4 flex items-center gap-2"><Receipt className="h-4 w-4 text-teal" /> Payment history</h2>
        {history.length === 0 ? (
          <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">No payments yet. Completed payments will show the date, amount, and exactly what they were applied to.</p>
        ) : (
          <div className="bg-surface border border-line rounded-2xl overflow-hidden">
            <div className="divide-y divide-line">
              {history.map((inv) => (
                <div key={inv.invoice_id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`history-row-${inv.invoice_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-navy">{inv.description}</p>
                    <p className="text-xs text-mutedink mt-0.5">
                      {inv.fee_label} · paid to <span className="font-medium text-ink">{inv.recipient}</span>
                      {caseTitle(inv.case_id) && <> · applied to: {caseTitle(inv.case_id)}</>}
                    </p>
                    {inv.status === "PAID" && inv.paid_at && (
                      <p className="text-xs text-sage mt-0.5">Paid {new Date(inv.paid_at).toLocaleString()} · card via Stripe</p>
                    )}
                  </div>
                  <p className="text-base font-serif font-semibold text-navy">${inv.amount.toFixed(2)}</p>
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full w-fit flex items-center gap-1 ${STATUS_STYLES[inv.status]}`} data-testid={`history-status-${inv.invoice_id}`}>
                    {inv.status === "VOID" && <Ban className="h-3 w-3" />} {inv.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <p className="text-xs text-mutedink leading-relaxed max-w-2xl" data-testid="payments-disclaimer">
        Professional and service fees are paid to CF Full Circle. Government filing fees, laboratory fees, courier fees, translation fees, and record retrieval fees are collected on behalf of the named recipient and are never represented as CF Full Circle fees. Payments are processed securely by Stripe; CF Full Circle never stores your card details.
      </p>
    </div>
  );
}
